/**
 * 颜色工具（零依赖、零 DOM）。
 *
 * 为什么自己写而不借 CSSOM：`getComputedStyle` 在 jsdom 下不可用，
 * 且我们处理的是**文档里的原始色值文本**（可能是 `#abc`、`rgb(1,2,3)`、`white`），
 * 不是渲染结果。行为必须可预期、可单测。
 */
export interface Rgba {
  /** 0-255 */
  r: number;
  g: number;
  b: number;
  /** 0-1 */
  a: number;
}

/** CSS 全部具名颜色（148 个），紧凑存放：`name:rrggbb,...` */
const NAMED_RAW =
  'aliceblue:f0f8ff,antiquewhite:faebd7,aqua:00ffff,aquamarine:7fffd4,azure:f0ffff,beige:f5f5dc,bisque:ffe4c4,black:000000,' +
  'blanchedalmond:ffebcd,blue:0000ff,blueviolet:8a2be2,brown:a52a2a,burlywood:deb887,cadetblue:5f9ea0,chartreuse:7fff00,' +
  'chocolate:d2691e,coral:ff7f50,cornflowerblue:6495ed,cornsilk:fff8dc,crimson:dc143c,cyan:00ffff,darkblue:00008b,' +
  'darkcyan:008b8b,darkgoldenrod:b8860b,darkgray:a9a9a9,darkgreen:006400,darkkhaki:bdb76b,darkmagenta:8b008b,' +
  'darkolivegreen:556b2f,darkorange:ff8c00,darkorchid:9932cc,darkred:8b0000,darksalmon:e9967a,darkseagreen:8fbc8f,' +
  'darkslateblue:483d8b,darkslategray:2f4f4f,darkturquoise:00ced1,darkviolet:9400d3,deeppink:ff1493,deepskyblue:00bfff,' +
  'dimgray:696969,dodgerblue:1e90ff,firebrick:b22222,floralwhite:fffaf0,forestgreen:228b22,fuchsia:ff00ff,gainsboro:dcdcdc,' +
  'ghostwhite:f8f8ff,gold:ffd700,goldenrod:daa520,gray:808080,green:008000,greenyellow:adff2f,honeydew:f0fff0,hotpink:ff69b4,' +
  'indigo:4b0082,ivory:fffff0,khaki:f0e68c,lavender:e6e6fa,lavenderblush:fff0f5,lawngreen:7cfc00,lemonchiffon:fffacd,' +
  'lightblue:add8e6,lightcoral:f08080,lightcyan:e0ffff,lightgoldenrodyellow:fafad2,lightgray:d3d3d3,lightgreen:90ee90,' +
  'lightpink:ffb6c1,lightsalmon:ffa07a,lightseagreen:20b2aa,lightskyblue:87cefa,lightslategray:778899,lightsteelblue:b0c4de,' +
  'lightyellow:ffffe0,lime:00ff00,limegreen:32cd32,linen:faf0e6,magenta:ff00ff,maroon:800000,mediumaquamarine:66cdaa,' +
  'mediumblue:0000cd,mediumorchid:ba55d3,mediumpurple:9370db,mediumseagreen:3cb371,mediumslateblue:7b68ee,' +
  'mediumspringgreen:00fa9a,mediumturquoise:48d1cc,mediumvioletred:c71585,midnightblue:191970,mintcream:f5fffa,mistyrose:ffe4e1,' +
  'moccasin:ffe4b5,navajowhite:ffdead,navy:000080,oldlace:fdf5e6,olive:808000,olivedrab:6b8e23,orange:ffa500,orangered:ff4500,' +
  'orchid:da70d6,palegoldenrod:eee8aa,palegreen:98fb98,paleturquoise:afeeee,palevioletred:db7093,papayawhip:ffefd5,' +
  'peachpuff:ffdab9,peru:cd853f,pink:ffc0cb,plum:dda0dd,powderblue:b0e0e6,purple:800080,rebeccapurple:663399,red:ff0000,' +
  'rosybrown:bc8f8f,royalblue:4169e1,saddlebrown:8b4513,salmon:fa8072,sandybrown:f4a460,seagreen:2e8b57,seashell:fff5ee,' +
  'sienna:a0522d,silver:c0c0c0,skyblue:87ceeb,slateblue:6a5acd,slategray:708090,snow:fffafa,springgreen:00ff7f,steelblue:4682b4,' +
  'tan:d2b48c,teal:008080,thistle:d8bfd8,tomato:ff6347,turquoise:40e0d0,violet:ee82ee,wheat:f5deb3,white:ffffff,' +
  'whitesmoke:f5f5f5,yellow:ffff00,yellowgreen:9acd32';

const NAMED: Map<string, Rgba> = (() => {
  const map = new Map<string, Rgba>();
  for (const pair of NAMED_RAW.split(',')) {
    const [name, hex] = pair.split(':') as [string, string];
    map.set(name, {
      r: Number.parseInt(hex.slice(0, 2), 16),
      g: Number.parseInt(hex.slice(2, 4), 16),
      b: Number.parseInt(hex.slice(4, 6), 16),
      a: 1,
    });
  }
  // 别名（CSS 定义 gray/grey 等价）
  map.set('grey', map.get('gray') as Rgba);
  map.set('darkgrey', map.get('darkgray') as Rgba);
  map.set('dimgrey', map.get('dimgray') as Rgba);
  map.set('lightgrey', map.get('lightgray') as Rgba);
  map.set('slategrey', map.get('slategray') as Rgba);
  map.set('darkslategrey', map.get('darkslategray') as Rgba);
  map.set('lightslategrey', map.get('lightslategray') as Rgba);
  return map;
})();

const clamp = (n: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, n));
const channel = (v: string): number | null => {
  const s = v.trim();
  if (s.endsWith('%')) {
    const p = Number.parseFloat(s.slice(0, -1));
    return Number.isFinite(p) ? clamp(Math.round((p / 100) * 255), 0, 255) : null;
  }
  const n = Number.parseFloat(s);
  return Number.isFinite(n) ? clamp(Math.round(n), 0, 255) : null;
};
const alpha = (v: string | undefined): number => {
  if (v === undefined) return 1;
  const s = v.trim();
  const n = s.endsWith('%') ? Number.parseFloat(s.slice(0, -1)) / 100 : Number.parseFloat(s);
  return Number.isFinite(n) ? clamp(n, 0, 1) : 1;
};

/** 解析色值文本 → Rgba；不认识（none / url(#g) / currentColor / 变量…）返回 null */
export function parseColor(value: string | null | undefined): Rgba | null {
  if (!value) return null;
  const s = value.trim().toLowerCase();
  if (s === '' || s === 'none' || s === 'currentcolor' || s === 'inherit') return null;
  if (s.startsWith('url(')) return null;
  if (s === 'transparent') return { r: 0, g: 0, b: 0, a: 0 };

  if (s.startsWith('#')) {
    const hex = s.slice(1);
    if (/^[0-9a-f]{3,4}$/.test(hex)) {
      const [r, g, b] = [hex[0], hex[1], hex[2]] as [string, string, string];
      return {
        r: Number.parseInt(r + r, 16),
        g: Number.parseInt(g + g, 16),
        b: Number.parseInt(b + b, 16),
        a: hex.length === 4 ? Number.parseInt((hex[3] as string) + (hex[3] as string), 16) / 255 : 1,
      };
    }
    if (/^[0-9a-f]{6}$/.test(hex)) {
      return {
        r: Number.parseInt(hex.slice(0, 2), 16),
        g: Number.parseInt(hex.slice(2, 4), 16),
        b: Number.parseInt(hex.slice(4, 6), 16),
        a: 1,
      };
    }
    if (/^[0-9a-f]{8}$/.test(hex)) {
      return {
        r: Number.parseInt(hex.slice(0, 2), 16),
        g: Number.parseInt(hex.slice(2, 4), 16),
        b: Number.parseInt(hex.slice(4, 6), 16),
        a: Number.parseInt(hex.slice(6, 8), 16) / 255,
      };
    }
    return null;
  }

  const fn = /^(rgba?|hsla?)\(([^)]*)\)$/.exec(s);
  if (fn) {
    const kind = fn[1] as string;
    const parts = (fn[2] as string).split(/[,/\s]+/).filter(Boolean);
    if (kind.startsWith('rgb')) {
      const [r, g, b] = [channel(parts[0] ?? ''), channel(parts[1] ?? ''), channel(parts[2] ?? '')];
      if (r === null || g === null || b === null) return null;
      return { r, g, b, a: alpha(parts[3]) };
    }
    const h = Number.parseFloat((parts[0] ?? '').replace('deg', ''));
    const sat = Number.parseFloat(parts[1] ?? '');
    const lig = Number.parseFloat(parts[2] ?? '');
    if (!Number.isFinite(h) || !Number.isFinite(sat) || !Number.isFinite(lig)) return null;
    const rgb = hslToRgb(h, clamp(sat, 0, 100) / 100, clamp(lig, 0, 100) / 100);
    return { ...rgb, a: alpha(parts[3]) };
  }

  return NAMED.get(s) ?? null;
}

/** Rgba → 十六进制文本（alpha<1 时输出 8 位） */
export function toHex(c: Rgba, opts: { alpha?: boolean } = {}): string {
  const h = (n: number): string => clamp(Math.round(n), 0, 255).toString(16).padStart(2, '0');
  const base = `#${h(c.r)}${h(c.g)}${h(c.b)}`;
  const withAlpha = opts.alpha ?? c.a < 1;
  return withAlpha ? `${base}${h(c.a * 255)}` : base;
}

/** 归一化：任何能认出的色值 → `#rrggbb`（带透明度时 `#rrggbbaa`）；认不出返回 null */
export const normalizeColor = (value: string | null | undefined): string | null => {
  const rgba = parseColor(value);
  return rgba ? toHex(rgba) : null;
};

/** WCAG 相对亮度（sRGB 线性化），0=黑 1=白 */
export function relativeLuminance(c: Rgba): number {
  const lin = (v: number): number => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * lin(c.r) + 0.7152 * lin(c.g) + 0.0722 * lin(c.b);
}

/** 中性色（黑白灰）：通道极差小于阈值 */
export const isNeutral = (c: Rgba, threshold = 14): boolean =>
  Math.max(c.r, c.g, c.b) - Math.min(c.r, c.g, c.b) <= threshold;

export function rgbToHsl(c: Rgba): { h: number; s: number; l: number } {
  const r = c.r / 255;
  const g = c.g / 255;
  const b = c.b / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return { h: 0, s: 0, l };
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h: number;
  if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
  else if (max === g) h = ((b - r) / d + 2) / 6;
  else h = ((r - g) / d + 4) / 6;
  return { h: h * 360, s, l };
}

export function hslToRgb(h: number, s: number, l: number): { r: number; g: number; b: number } {
  const hue = ((h % 360) + 360) % 360 / 360;
  if (s === 0) {
    const v = Math.round(l * 255);
    return { r: v, g: v, b: v };
  }
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const toRgb = (t: number): number => {
    let x = t;
    if (x < 0) x += 1;
    if (x > 1) x -= 1;
    if (x < 1 / 6) return p + (q - p) * 6 * x;
    if (x < 1 / 2) return q;
    if (x < 2 / 3) return p + (q - p) * (2 / 3 - x) * 6;
    return p;
  };
  return {
    r: Math.round(toRgb(hue + 1 / 3) * 255),
    g: Math.round(toRgb(hue) * 255),
    b: Math.round(toRgb(hue - 1 / 3) * 255),
  };
}

/** 保持色相/饱和度，换亮度（0-1） */
export function withLightness(c: Rgba, l: number): Rgba {
  const { h, s } = rgbToHsl(c);
  return { ...hslToRgb(h, s, clamp(l, 0, 1)), a: c.a };
}

/** HSL 亮度 0-1 */
export const lightness = (c: Rgba): number => rgbToHsl(c).l;

/** 色相距离（0-180） */
export function hueDistance(a: Rgba, b: Rgba): number {
  const ha = rgbToHsl(a).h;
  const hb = rgbToHsl(b).h;
  const d = Math.abs(ha - hb) % 360;
  return d > 180 ? 360 - d : d;
}
